// material
import {
  Container,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  Slide,
  DialogTitle,
  DialogActions,
  DialogContentText
} from '@mui/material';
// hooks
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import { PATH_DASHBOARD } from '../../routes/paths';
import { checkRoles, ROLE_USER } from 'guards/CheckRoles';
import { useTranslation } from 'react-i18next';
import SvgIconStyle from '../../components/SvgIconStyle';
import LoadingSpinner from '../../components/LoadingSpinner';
import { RootState, useSelector } from '../../redux/store';
import * as React from 'react';
import { useState } from 'react';
import { Device } from '../../@types/model/device';
import axios from '../../utils/axios';
import { MIconButton } from 'components/@material-extend';
import { TransitionProps } from '@mui/material/transitions';
import { DialogContent } from '@mui/material';
import closeFill from '@iconify/icons-eva/close-fill';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { DeviceTag } from '../../@types/model/user';

const Transition: any = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
// ----------------------------------------------------------------------

export default function SharedList() {
  const { themeStretch } = useSettings();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const [loading, setLoading] = useState(true);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);

  const [tags, setTags] = useState<DeviceTag[]>([]);
  const [tagToDelete, setTagToDelete] = useState<DeviceTag | null>(null);

  const role = checkRoles(user, ROLE_USER) ? '/' : '/end-user';

  React.useEffect(() => {
    axios
      .get('/api/v2/user/shared-devices')
      .then(({ data }) => {
        let devices = data.data.map((device: any) => {
          let owners = data.included.filter((include: any) => {
            if (include.type === 'App\\Domain\\Model\\User\\User') {
              let owner = device.attributes.owner;
              if (owner.split(':').length === 3) {
                if (owner.split(':')[2] === include.guid) return true;
              }
            }
            return false;
          });
          if (owners.length > 0) {
            return {
              guid: device?.guid,
              attributes: {
                ...device?.attributes,
                owner: {
                  guid: owners[0].guid,
                  name: owners[0].attributes.name,
                  email: owners[0].attributes.email
                }
              }
            } as Device;
          } else {
            return {
              guid: device?.guid,
              attributes: {
                ...device?.attributes
              }
            } as Device;
          }
        });
        setDevices(devices);
        setLoadingDelete(false);
        setDeviceToDelete(null);
        setLoadingDevices(false);
      })
      .catch((e: any) => {
        enqueueSnackbar(e.detail, {
          variant: 'error',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setLoadingDelete(false);
        setLoadingDevices(false);
      });
    axios
      .get('/api/v2/user/shared-tags')
      .then(({ data }) => {
        let tags = data.data.map((tag: any) => {
          let owners = data.included.filter((include: any) => {
            if (include.type === 'App\\Domain\\Model\\User\\User') {
              let owner = tag.attributes.owner;
              if (owner.split(':').length === 3) {
                if (owner.split(':')[2] === include.guid) return true;
              }
            }
            return false;
          });
          if (owners.length > 0) {
            return {
              guid: tag?.guid,
              attributes: {
                ...tag?.attributes,
                owner: {
                  guid: owners[0].guid,
                  name: owners[0].attributes.name,
                  email: owners[0].attributes.email
                }
              }
            } as DeviceTag;
          } else {
            return {
              guid: tag?.guid,
              attributes: {
                ...tag?.attributes
              }
            } as DeviceTag;
          }
        });
        setTags(tags);
        setLoadingDelete(false);
        setTagToDelete(null);
        setLoading(false);
      })
      .catch((e: any) => {
        enqueueSnackbar(e.detail, {
          variant: 'error',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setLoadingDelete(false);
        setLoading(false);
      });
  }, []);

  function deleteDeviceSharedRequest(d: Device | null) {
    setLoadingDelete(true);
    axios
      .delete('/api/v2/device/' + d?.guid)
      .then(() => {
        enqueueSnackbar(t('device_shared_deleted'), {
          variant: 'success',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setDevices(
          devices.filter((d: Device) => {
            if (d?.guid === deviceToDelete?.guid) return false;
            return true;
          })
        );
        setLoadingDelete(false);
        setDeviceToDelete(null);
      })
      .catch((e: any) => {
        enqueueSnackbar(e.detail, {
          variant: 'error',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setLoadingDelete(false);
      });
  }

  function deleteTagSharedRequest(tag: DeviceTag | null) {
    setLoadingDelete(true);
    axios
      .delete('/api/v2/device-tag/' + tag?.guid)
      .then(() => {
        enqueueSnackbar(t('device_shared_deleted'), {
          variant: 'success',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setTags(
          tags.filter((t: DeviceTag) => {
            if (tag?.guid === t?.guid) return false;
            return true;
          })
        );
        setLoadingDelete(false);
        setTagToDelete(null);
      })
      .catch((e: any) => {
        enqueueSnackbar(e.detail, {
          variant: 'error',
          action: (key: any) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          )
        });
        setLoadingDelete(false);
      });
  }

  const pageTitle = t('device_and_tags_shared');
  return (
    <Page title={pageTitle}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading={pageTitle}
          links={[
            { name: t('home'), href: role },
            { name: pageTitle, href: PATH_DASHBOARD.deliveryConfigurations.list }
          ]}
        />
        <h3 style={{ marginTop: 20, marginBottom: 20, paddingLeft: 10 }}>
          {t('device_tags_shared')}
        </h3>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('shared_with')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((t: DeviceTag) => {
                return (
                  <TableRow key={t.guid}>
                    <TableCell>{t?.attributes.name}</TableCell>
                    <TableCell>
                      {t.attributes.owner?.name}
                      {t.attributes.owner?.email ? (
                        <React.Fragment>
                          <br />({t.attributes.owner?.email})
                        </React.Fragment>
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setTagToDelete(t);
                        }}
                      >
                        <SvgIconStyle
                          src="/static/icons/giraf/remove.svg"
                          sx={{
                            width: '20px',
                            height: '20px',
                            color: 'red'
                          }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <h3 style={{ marginTop: 20, marginBottom: 20, paddingLeft: 10 }}>{t('device_shared')}</h3>
        {loadingDevices ? (
          <LoadingSpinner />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('identifier')}</TableCell>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('status')}</TableCell>
                <TableCell>{t('shared_with')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((d: Device) => {
                return (
                  <TableRow key={d.guid}>
                    <TableCell>{d?.attributes.identifier}</TableCell>
                    <TableCell>{d?.attributes.name}</TableCell>
                    <TableCell>{t(d.attributes.state)}</TableCell>
                    <TableCell>
                      {d.attributes.owner?.name}
                      <br />({d.attributes.owner?.email})
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setDeviceToDelete(d);
                        }}
                      >
                        <SvgIconStyle
                          src="/static/icons/giraf/remove.svg"
                          sx={{
                            width: '20px',
                            height: '20px',
                            color: 'red'
                          }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <Dialog
          open={deviceToDelete !== null}
          TransitionComponent={Transition}
          keepMounted
          onClose={() => {
            setDeviceToDelete(null);
          }}
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle>{t('device_shared_delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              {t('device_shared_delete_dialog')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeviceToDelete(null);
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              variant={'contained'}
              loading={loadingDelete}
              color="error"
              onClick={() => {
                deleteDeviceSharedRequest(deviceToDelete);
              }}
            >
              {t('confirm')}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={tagToDelete !== null}
          TransitionComponent={Transition}
          keepMounted
          onClose={() => {
            setTagToDelete(null);
          }}
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle>{t('tag_shared_delete')}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              {t('tag_shared_delete_dialog')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setTagToDelete(null);
              }}
            >
              {t('cancel')}
            </Button>
            <LoadingButton
              variant={'contained'}
              loading={loadingDelete}
              color="error"
              onClick={() => {
                deleteTagSharedRequest(tagToDelete);
              }}
            >
              {t('confirm')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
